/**
 * Shared form validation utilities for lead/tour form blocks.
 *
 * Phone numbers are restricted to exactly 10 US digits (no country code)
 * because downstream routers (Entrata, RentCafe, etc.) reject country codes.
 *
 * Email validation rejects malformed domains such as consecutive dots.
 */

// Matches a valid email: no consecutive dots in local or domain parts,
// domain has at least one dot, TLD is 2+ alpha chars.
const EMAIL_RE =
  /^[A-Za-z0-9](?:[A-Za-z0-9._%+-]*[A-Za-z0-9])?@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

/** Strip everything except digits. */
export function stripNonDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Drop leading digits that can never start a NANP area code — a 1 is a country
 * code, a 0 is a typo. Exported separately from normalizePhoneDigits so callers
 * doing caret math can tell how many characters came off the front.
 */
export function stripPhonePrefix(digits: string): string {
  return digits.replace(/^[01]+/, '');
}

/**
 * Reduce any typed, pasted or autofilled value to at most 10 US digits.
 *
 * The country code is removed *before* the 10-digit cap. Applying the cap first
 * is what turns an autofilled "+1 (678) 823-0350" into "1678823035" — a number
 * that still passes a length check but is missing its last digit.
 */
export function normalizePhoneDigits(value: string): string {
  return stripPhonePrefix(stripNonDigits(value)).slice(0, 10);
}

// Obvious placeholder / fake numbers to reject even though they have 10 digits.
const FAKE_PHONE_NUMBERS = new Set([
  '1234567890', // 123-456-7890 sequential placeholder
]);

/** True when all 10 digits are identical: 0000000000, 1111111111, 5555555555… */
function isAllSameDigit(digits: string): boolean {
  return /^(\d)\1{9}$/.test(digits);
}

/**
 * Validate a US phone number: exactly 10 digits (no country code), a real NANP
 * area code, and not an obvious placeholder (all-same-digit or a known fake
 * like 123-456-7890).
 */
export function validatePhone(value: string): boolean {
  const digits = stripNonDigits(value);
  if (digits.length !== 10) return false;
  // NANP area codes start with 2-9. A leading 0 or 1 means the value is a typo
  // or a country code that swallowed the last digit (1678823035).
  if (!/^[2-9]/.test(digits)) return false;
  if (isAllSameDigit(digits)) return false; // 0000000000 … 9999999999 (incl. 5555555555)
  if (FAKE_PHONE_NUMBERS.has(digits)) return false;
  return true;
}

/** Validate an email address, rejecting consecutive dots and other malformed patterns. */
export function validateEmail(value: string): boolean {
  if (!value) return false;
  if (value.includes('..')) return false;
  return EMAIL_RE.test(value);
}

/** RentCafe API v2 caps first/last name at 40 (Create Lead, Create Appointment); forms can't tell which PMS they feed, so all cap here. */
export const NAME_MAX_LENGTH = 40;

// Entrata refuses names with emoji, symbols like ☆, or @ (usually an email typed into the name field).
const NAME_REJECTED_RE = /[\p{Extended_Pictographic}\p{So}@]/u;

export const NAME_ERROR_MESSAGE = "Names can't include emoji or symbols";

/** Validate a first or last name against what every PMS accepts. */
export function validateName(value: string): boolean {
  return value.length <= NAME_MAX_LENGTH && !NAME_REJECTED_RE.test(value);
}

/** Max character length for free-text message fields. */
export const MESSAGE_MAX_LENGTH = 512;

/** Validate a free-text message field (optional — empty is valid, over limit is not). */
export function validateMessage(value: string): boolean {
  return !value || value.length <= MESSAGE_MAX_LENGTH;
}

/**
 * Format a digit string as (123) 456-7890.
 * Partial strings are formatted progressively:
 *   "123"       → "(123"
 *   "1234"      → "(123) 4"
 *   "1234567890" → "(123) 456-7890"
 */
export function formatPhoneDisplay(digits: string): string {
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}
